<template>
    <div class="portal">
        <!-- Decorative corner shapes, purely visual -->
        <div class="deco deco-blue" aria-hidden="true"></div>
        <div class="deco deco-pink" aria-hidden="true"></div>

        <div class="shell">
            <!-- Language switch -->
            <div class="lang-row">
                <div class="lang-toggle" role="group" aria-label="Language">
                    <button v-for="code in locales" :key="code" type="button" class="lang-btn"
                        :class="{ active: locale === code }" :aria-pressed="locale === code"
                        @click="setLocale(code)">
                        {{ code.toUpperCase() }}
                    </button>
                </div>
            </div>

            <header class="hero">
                <h1 class="tagline">
                    {{ t('tagline1') }}
                    <span class="tagline-sub">{{ t('tagline2') }}</span>
                </h1>
                <img class="hero-logo" src="img/logo.png" alt="WashXpress" />
            </header>

            <!-- ------------------------------------------------ step: phone -->
            <form v-if="step === 'phone'" class="card-form" novalidate @submit.prevent="onSubmitPhone">
                <label class="field" :class="{ invalid: !!errorMessage }">
                    <span class="field-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="22" height="22">
                            <path fill="#204496"
                                d="M6.6 10.8c1.4 2.8 3.8 5.1 6.6 6.6l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.6.6.6 0 1 .4 1 1V20c0 .6-.4 1-1 1-9.4 0-17-7.6-17-17 0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.6.1.3 0 .7-.2 1l-2.3 2.2z" />
                        </svg>
                    </span>
                    <input ref="phoneInput" v-model="phoneNumber" type="tel" inputmode="numeric"
                        autocomplete="tel" maxlength="12" :placeholder="t('phonePlaceholder')"
                        :aria-label="t('phonePlaceholder')" :aria-invalid="!!errorMessage"
                        @input="onPhoneInput" />
                </label>

                <p v-if="errorMessage" class="msg msg-error" role="alert">{{ errorMessage }}</p>
                <p v-else class="msg msg-hint">{{ t('phoneHint') }}</p>

                <button class="btn-primary" type="submit" :disabled="!isPhoneValid || busy">
                    {{ busy ? t('sending') : t('confirm') }}
                </button>
            </form>

            <!-- ------------------------------------------- step: not a member -->
            <div v-else-if="step === 'notMember'" class="card-form">
                <div class="notice" role="alert">
                    <span class="notice-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="30" height="30">
                            <path fill="#c2410c"
                                d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 5a1.1 1.1 0 0 1 1.1 1.1v5a1.1 1.1 0 0 1-2.2 0v-5A1.1 1.1 0 0 1 12 7zm0 10.9a1.3 1.3 0 1 1 0-2.6 1.3 1.3 0 0 1 0 2.6z" />
                        </svg>
                    </span>
                    <h2 class="notice-title">{{ t('notMemberTitle') }}</h2>
                    <p class="notice-body">{{ t('notMemberBody', { phone: formattedPhone }) }}</p>
                    <p class="notice-action">{{ t('notMemberAction') }}</p>
                    <p class="notice-hint">{{ t('notMemberHint') }}</p>
                </div>

                <button class="btn-primary" type="button" @click="useAnotherNumber">
                    {{ t('useAnotherNumber') }}
                </button>
            </div>

            <!-- -------------------------------------------------- step: otp -->
            <form v-else-if="step === 'otp'" class="card-form" novalidate @submit.prevent="onSubmitOtp">
                <h2 class="step-title">{{ t('otpTitle') }}</h2>
                <p class="step-sub">
                    {{ t('otpSentTo') }} <strong>{{ formattedPhone }}</strong>
                </p>
                <p v-if="referenceCode" class="step-ref">
                    {{ t('otpRefCode') }}: <strong>{{ referenceCode }}</strong>
                </p>

                <label class="field field-otp" :class="{ invalid: !!errorMessage }">
                    <input ref="otpInput" v-model="otpCode" type="text" inputmode="numeric"
                        autocomplete="one-time-code" :maxlength="otpLength"
                        :placeholder="t('otpPlaceholder')" :aria-label="t('otpPlaceholder')"
                        :aria-invalid="!!errorMessage" @input="onOtpInput" />
                </label>

                <p v-if="errorMessage" class="msg msg-error" role="alert">{{ errorMessage }}</p>
                <p v-else class="msg msg-hint">{{ t('otpHint') }}</p>

                <button class="btn-primary" type="submit" :disabled="!isOtpValid || busy">
                    {{ busy ? t('verifying') : t('confirm') }}
                </button>

                <div class="actions">
                    <button type="button" class="link-btn" :disabled="resendCountdown > 0 || busy"
                        @click="onResend">
                        {{ resendCountdown > 0 ? t('resendIn', { seconds: resendCountdown }) : t('resend') }}
                    </button>
                    <button type="button" class="link-btn" :disabled="busy" @click="backToPhone">
                        {{ t('changeNumber') }}
                    </button>
                </div>
            </form>

            <!-- ---------------------------------------------- step: connect -->
            <div v-else class="card-form connecting">
                <div class="spinner" aria-hidden="true"></div>
                <p class="msg msg-hint">{{ t('connecting') }}</p>
            </div>

            <p v-if="hotspotMissing" class="msg msg-warn">{{ t('errNoHotspot') }}</p>
        </div>
    </div>
</template>

<script>
import { hexMD5 } from "@/assets/js/md5.js";
import { OTP_LENGTH, OTP_RESEND_SECONDS } from "@/config";
import { LOCALES, detectLocale, persistLocale, translate } from "@/i18n";
import { requestOtp, verifyOtp } from "@/services/wifiAuth";

// error_code values from freeradius-api that need bespoke copy. Everything else
// falls back to the message the API already localised. NOT_A_MEMBER is absent
// on purpose: it gets its own step rather than an inline message.
const ERROR_COPY = {
    NETWORK_ERROR: "errNetwork",
    UPSTREAM_UNAVAILABLE: "errNetwork",
};

export default {
    name: "LoginUser",
    data() {
        return {
            locale: detectLocale(),
            locales: LOCALES,
            otpLength: OTP_LENGTH,

            step: "phone", // phone | notMember | otp | connecting
            phoneNumber: "",
            otpCode: "",
            referenceCode: "",
            errorMessage: "",
            busy: false,

            resendCountdown: 0,
            resendTimer: null,

            // Handed over by the Mikrotik hotspot login page as query params.
            hotspot: {
                chapId: "",
                chapChallenge: "",
                linkLoginOnly: "",
                linkOrig: "",
            },
        };
    },
    computed: {
        /** Digits only, so validation and submission share one representation. */
        normalizedPhone() {
            return this.phoneNumber.replace(/\D/g, "");
        },
        isPhoneValid() {
            return /^0[689]\d{8}$/.test(this.normalizedPhone);
        },
        isOtpValid() {
            return new RegExp(`^\\d{${this.otpLength}}$`).test(this.otpCode);
        },
        formattedPhone() {
            const n = this.normalizedPhone;
            return n.length === 10 ? `${n.slice(0, 3)}-${n.slice(3, 6)}-${n.slice(6)}` : n;
        },
        /** True when the page was opened directly rather than via the hotspot. */
        hotspotMissing() {
            return !this.hotspot.linkLoginOnly;
        },
    },
    mounted() {
        const params = new URLSearchParams(window.location.search);
        this.hotspot.chapId = params.get("chap-id") || "";
        this.hotspot.chapChallenge = params.get("chap-challenge") || "";
        this.hotspot.linkLoginOnly = params.get("link-login-only") || "";
        this.hotspot.linkOrig = params.get("link-orig") || "";

        // Mikrotik re-renders login.html with ?error=... after a failed attempt.
        const hotspotError = params.get("error");
        if (hotspotError) {
            this.errorMessage = hotspotError;
        }

        // The previous flow used the phone number as the RADIUS username, so
        // Mikrotik may replay it. Prefill to save the user some typing.
        const username = params.get("username") || "";
        if (/^0\d{9}$/.test(username)) {
            this.phoneNumber = username;
        }
    },
    beforeUnmount() {
        this.stopResendCountdown();
    },
    methods: {
        t(key, params) {
            return translate(this.locale, key, params);
        },
        setLocale(code) {
            this.locale = code;
            persistLocale(code);
        },

        onPhoneInput() {
            // Keep digits only; users paste numbers with spaces and dashes.
            this.phoneNumber = this.phoneNumber.replace(/\D/g, "").slice(0, 10);
            this.errorMessage = "";
        },
        onOtpInput() {
            this.otpCode = this.otpCode.replace(/\D/g, "").slice(0, this.otpLength);
            this.errorMessage = "";
        },

        /** Maps an API failure onto display copy. */
        describeError(result) {
            const key = ERROR_COPY[result?.error_code];
            if (key) return this.t(key);
            if (typeof result?.error === "string" && result.error.trim()) {
                return result.error;
            }
            return this.t("errGeneric");
        },

        async onSubmitPhone() {
            if (!this.isPhoneValid) {
                this.errorMessage = this.t("phoneInvalid");
                return;
            }
            await this.sendOtp();
        },

        async sendOtp() {
            this.busy = true;
            this.errorMessage = "";
            try {
                const result = await requestOtp(this.normalizedPhone);
                if (!result.success) {
                    // The API screens membership before spending an SMS, so a
                    // stranger's number stops here with a full explanation
                    // rather than a one-line validation error.
                    if (result.error_code === "NOT_A_MEMBER") {
                        this.stopResendCountdown();
                        this.step = "notMember";
                        return;
                    }
                    this.errorMessage = this.describeError(result);
                    // The API reports how long until another send is allowed.
                    if (typeof result.cooldown === "number" && result.cooldown > 0) {
                        this.startResendCountdown(result.cooldown);
                        this.step = "otp";
                    }
                    return;
                }
                this.referenceCode = result.reference_code || "";
                this.otpCode = "";
                this.step = "otp";
                this.startResendCountdown(OTP_RESEND_SECONDS);
                this.$nextTick(() => this.$refs.otpInput?.focus());
            } finally {
                this.busy = false;
            }
        },

        async onResend() {
            if (this.resendCountdown > 0) return;
            await this.sendOtp();
        },

        async onSubmitOtp() {
            if (!this.isOtpValid) {
                this.errorMessage = this.t("otpInvalid");
                return;
            }
            this.busy = true;
            this.errorMessage = "";
            try {
                const result = await verifyOtp(this.normalizedPhone, this.otpCode);
                if (!result.success) {
                    // Reachable when the pre-check failed open on an upstream
                    // blip, or when membership lapsed mid-flow.
                    if (result.error_code === "NOT_A_MEMBER") {
                        this.stopResendCountdown();
                        this.otpCode = "";
                        this.step = "notMember";
                        return;
                    }
                    this.errorMessage = this.describeError(result);
                    this.otpCode = "";
                    return;
                }
                // The RADIUS user now exists with this password. Hand it to the
                // router to actually open the connection.
                this.submitToHotspot(result.username, result.password);
            } finally {
                this.busy = false;
            }
        },

        /** Back to the phone step, keeping the number so it can be corrected. */
        backToPhone() {
            this.stopResendCountdown();
            this.step = "phone";
            this.otpCode = "";
            this.referenceCode = "";
            this.errorMessage = "";
            this.$nextTick(() => this.$refs.phoneInput?.focus());
        },

        /** Back to an empty phone step, for entering a different number. */
        useAnotherNumber() {
            this.phoneNumber = "";
            this.backToPhone();
        },

        startResendCountdown(seconds) {
            this.stopResendCountdown();
            this.resendCountdown = Math.ceil(seconds);
            this.resendTimer = window.setInterval(() => {
                this.resendCountdown -= 1;
                if (this.resendCountdown <= 0) this.stopResendCountdown();
            }, 1000);
        },
        stopResendCountdown() {
            if (this.resendTimer) {
                window.clearInterval(this.resendTimer);
                this.resendTimer = null;
            }
            this.resendCountdown = 0;
        },

        /**
         * Posts the credentials to the Mikrotik login endpoint. CHAP is used
         * when the router supplied a challenge, otherwise the password goes as
         * plaintext over PAP, exactly as the router expects.
         */
        submitToHotspot(username, password) {
            if (!this.hotspot.linkLoginOnly) {
                this.errorMessage = this.t("errNoHotspot");
                return;
            }
            this.step = "connecting";

            const form = document.createElement("form");
            form.method = "POST";
            form.action = this.hotspot.linkLoginOnly;

            const credential =
                this.hotspot.chapId !== ""
                    ? hexMD5(`${this.hotspot.chapId}${password}${this.hotspot.chapChallenge}`)
                    : password;

            form.appendChild(this.createInput("username", username));
            form.appendChild(this.createInput("password", credential));
            form.appendChild(this.createInput("dst", this.hotspot.linkOrig));
            form.appendChild(this.createInput("popup", "true"));

            document.body.appendChild(form);
            form.submit();
        },
        createInput(name, value) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = name;
            input.value = value;
            return input;
        },
    },
};
</script>

<style scoped>
.portal {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    background: #eef1f9;
    font-family: "Noto Sans Thai", "Prompt", -apple-system, BlinkMacSystemFont, "Segoe UI",
        sans-serif;
}

/* Decorative corner shapes */
.deco {
    position: absolute;
    pointer-events: none;
}

.deco-blue {
    top: -90px;
    left: -110px;
    width: 230px;
    height: 230px;
    background: #204496;
    border-radius: 0 0 100% 0;
    transform: rotate(-12deg);
}

.deco-pink {
    top: 40px;
    left: -70px;
    width: 180px;
    height: 180px;
    border: 26px solid #f7a8bb;
    border-radius: 50%;
    border-right-color: transparent;
    border-top-color: transparent;
}

.shell {
    position: relative;
    z-index: 1;
    max-width: 420px;
    margin: 0 auto;
    padding: 16px 24px 40px;
}

/* Language switch */
.lang-row {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 28px;
}

.lang-toggle {
    display: inline-flex;
    background: #ffffff;
    border-radius: 999px;
    padding: 4px;
    box-shadow: 0 2px 6px rgba(32, 68, 150, 0.12);
}

.lang-btn {
    min-width: 54px;
    padding: 8px 14px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #6b7694;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
}

.lang-btn.active {
    background: #204496;
    color: #ffffff;
}

.lang-btn:focus-visible {
    outline: 2px solid #204496;
    outline-offset: 2px;
}

/* Hero */
.hero {
    text-align: center;
    margin-bottom: 28px;
}

.tagline {
    margin: 0 0 12px;
    color: #204496;
    font-size: 30px;
    font-weight: 800;
    line-height: 1.25;
}

.tagline-sub {
    display: block;
    font-size: 17px;
    font-weight: 600;
}

.hero-logo {
    max-width: 230px;
    width: 70%;
    height: auto;
}

/* Form */
.card-form {
    text-align: center;
}

.field {
    position: relative;
    display: flex;
    align-items: center;
    background: #ffffff;
    border: 1px solid transparent;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(32, 68, 150, 0.1);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.field:focus-within {
    border-color: #204496;
    box-shadow: 0 0 0 3px rgba(32, 68, 150, 0.15);
}

.field.invalid {
    border-color: #e04f5f;
}

.field-icon {
    display: flex;
    align-items: center;
    padding: 0 10px 0 18px;
}

.field input {
    flex: 1;
    min-width: 0;
    height: 58px;
    padding: 0 18px 0 4px;
    border: 0;
    border-radius: 12px;
    background: transparent;
    color: #20304f;
    font-size: 17px;
    text-align: center;
    outline: none;
}

.field input::placeholder {
    color: #9aa4bd;
}

.field-otp input {
    padding-left: 18px;
    letter-spacing: 8px;
    font-size: 22px;
    font-weight: 700;
}

/* Messages */
.msg {
    margin: 14px 0 20px;
    font-size: 15px;
    line-height: 1.5;
}

.msg-hint {
    color: #204496;
    font-weight: 600;
}

.msg-error {
    color: #d23c4e;
    font-weight: 600;
}

.msg-warn {
    margin-top: 24px;
    color: #9a6a00;
    background: #fff6e0;
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 14px;
}

/* Buttons */
.btn-primary {
    width: 100%;
    height: 56px;
    border: 0;
    border-radius: 999px;
    background: #204496;
    color: #ffffff;
    font-size: 18px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s ease;
}

.btn-primary:hover:not(:disabled),
.btn-primary:focus-visible:not(:disabled) {
    background: #16327a;
}

.btn-primary:disabled {
    background: #a9b4cd;
    cursor: not-allowed;
}

.btn-primary:focus-visible {
    outline: 2px solid #16327a;
    outline-offset: 2px;
}

/* Not-a-member notice */
.notice {
    margin-bottom: 24px;
    padding: 24px 20px;
    background: #fff8ed;
    border: 1px solid #f6c89a;
    border-radius: 14px;
    text-align: center;
}

.notice-icon {
    display: inline-flex;
    margin-bottom: 10px;
}

.notice-title {
    margin: 0 0 10px;
    color: #9a3412;
    font-size: 20px;
    font-weight: 800;
}

.notice-body {
    margin: 0 0 10px;
    color: #7c2d12;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.6;
}

.notice-action {
    margin: 0 0 14px;
    color: #7c2d12;
    font-size: 15px;
    line-height: 1.7;
}

.notice-hint {
    margin: 0;
    padding-top: 12px;
    border-top: 1px solid #f6c89a;
    color: #9a6a3a;
    font-size: 13px;
    line-height: 1.6;
}

/* OTP step */
.step-title {
    margin: 0 0 6px;
    color: #204496;
    font-size: 22px;
    font-weight: 800;
}

.step-sub,
.step-ref {
    margin: 0 0 6px;
    color: #3f4a66;
    font-size: 15px;
}

.step-ref {
    margin-bottom: 20px;
    color: #6b7694;
}

.actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 18px;
}

.link-btn {
    border: 0;
    background: transparent;
    color: #204496;
    font-size: 15px;
    font-weight: 600;
    text-decoration: underline;
    cursor: pointer;
    padding: 6px;
}

.link-btn:disabled {
    color: #9aa4bd;
    text-decoration: none;
    cursor: not-allowed;
}

.link-btn:focus-visible {
    outline: 2px solid #204496;
    outline-offset: 2px;
}

/* Connecting */
.connecting {
    padding: 40px 0;
}

.spinner {
    width: 44px;
    height: 44px;
    margin: 0 auto;
    border: 4px solid rgba(32, 68, 150, 0.2);
    border-top-color: #204496;
    border-radius: 50%;
    animation: spin 0.9s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .spinner {
        animation-duration: 3s;
    }
}

@media (max-width: 360px) {
    .tagline {
        font-size: 26px;
    }

    .shell {
        padding-left: 16px;
        padding-right: 16px;
    }
}
</style>
